// ConfirmarGasto.js — o "tem certeza?" de todo gasto de Coins e Tickets no site.
//
// ================= POR QUE EXISTE (pedido do dono, 11/09/2026) =================
//
// Pagar com dinheiro já tem uma confirmação natural: a tela do gateway, onde a
// pessoa ainda pode desistir. Coins e Tickets não têm — o clique no botão É o
// gasto. "500" num botão laranja, clicado sem querer, trocava na hora 500
// Coins por um Ticket, e não havia caminho de volta sem pedir ao suporte.
//
// Então todo lugar que GASTA crédito passa por aqui antes de chamar o
// servidor: a troca de Coins por Tickets (Loja de Créditos), o uso de um Ticket
// no Clube (página do Clube e aba do Clube no perfil) e as Coins usadas como
// desconto no checkout.
//
// ================= O QUE A CAIXA MOSTRA =================
//
// O que sai, o que entra e como fica o saldo, em linhas — "Você paga 500
// Coins / Você recebe 1 Ticket / Saldo 9579 → 9079". É a conta que a pessoa
// faria de cabeça, feita para ela. Uma caixa que só pergunta "Confirmar?"
// ensina a clicar "sim" sem ler.
//
// ================= COMO SE USA =================
//
//   const { confirmar, dialogo } = useConfirmarGasto();
//   ...
//   if (!(await confirmar({ titulo, linhas, nota, textoConfirmar, tom }))) return;
//   ...
//   return <>{...tela}{dialogo}</>;
//
// `confirmar` devolve uma Promise<boolean>: `true` no botão de confirmar,
// `false` em Cancelar, Esc ou clique fora. O diálogo vai para o <body> por
// portal: dentro de um bloco com `transform` (as animações de entrada do site
// usam), `position: fixed` passaria a valer em relação ao bloco, e a caixa
// abriria no meio do card em vez de no meio da tela.
import React, { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";

// Classes ESCRITAS POR INTEIRO: o Tailwind só gera o que encontra literalmente.
const TONS = {
  ticket: {
    icone: "bi-ticket-perforated-fill",
    selo: "border-insigne-ticket/40 bg-insigne-ticket/15 text-insigne-ticket",
    botao: "bg-insigne-ticket text-insigne-ticket-contraste hover:opacity-90",
  },
  coin: {
    icone: "bi-coin",
    selo: "border-insigne-orange/40 bg-insigne-orange/15 text-insigne-orange",
    botao: "bg-insigne-orange text-paper hover:bg-insigne-orange-dark",
  },
};

/** "Tickets"/"Ticket" e "Coins", na cor de cada crédito — a regra do site. */
export const Tickets = ({ n }) => (
  <span className="font-bold text-insigne-ticket">
    {n != null && `${n} `}
    {Number(n) === 1 ? "Ticket" : "Tickets"}
  </span>
);
export const Coins = ({ n }) => (
  <span className="font-bold text-insigne-orange">
    {n != null && `${n} `}Coins
  </span>
);

const dataBR = (d) => d.toLocaleDateString("pt-BR");

/**
 * As opções do diálogo de USAR UM TICKET NO CLUBE — a mesma caixa na página do
 * Clube e na aba do Clube do perfil. A data nova sai da mesma regra do
 * servidor (AssinaturaService::ativar): os dias SOMAM ao fim atual quando o
 * Clube ainda vale, e contam de hoje quando não vale mais.
 */
export const opcoesUsarTicket = ({ saldoTickets, dias, fim, ativa }) => {
  const agora = new Date();
  const fimAtual = fim ? new Date(String(fim).replace(" ", "T")) : null;
  const base = ativa && fimAtual && !isNaN(fimAtual) && fimAtual > agora ? fimAtual : agora;
  const novoFim = new Date(base.getTime() + (Number(dias) || 30) * 86400000);
  const saldo = Number(saldoTickets) || 0;

  return {
    tom: "ticket",
    titulo: ativa ? "Renovar o Clube com 1 Ticket" : "Entrar no Clube com 1 Ticket",
    linhas: [
      { rotulo: "Você usa", valor: <Tickets n={1} /> },
      { rotulo: "Seu Clube ganha", valor: <strong>{Number(dias) || 30} dias</strong> },
      { rotulo: "Passa a valer até", valor: <strong>{dataBR(novoFim)}</strong> },
      { rotulo: "Seus Tickets", valor: <>{saldo} → <Tickets n={Math.max(0, saldo - 1)} /></> },
    ],
    nota: ativa
      ? "Os dias somam ao que você já tem. Nada do que já estava pago se perde."
      : undefined,
    textoConfirmar: "Usar 1 Ticket",
  };
};

export default function useConfirmarGasto() {
  // { titulo, linhas, nota, textoConfirmar, tom, resolver }
  const [pedido, setPedido] = useState(null);

  const confirmar = useCallback(
    (opcoes) => new Promise((resolver) => setPedido({ ...opcoes, resolver })),
    []
  );

  const responder = useCallback((sim) => {
    setPedido((p) => {
      if (p) p.resolver(sim);
      return null;
    });
  }, []);

  // Esc cancela, como em todo diálogo. E a página atrás não rola enquanto a
  // caixa está aberta — pela classe que o resto do site usa (App.css).
  useEffect(() => {
    if (!pedido) return;
    const aoTeclar = (e) => { if (e.key === "Escape") responder(false); };
    document.addEventListener("keydown", aoTeclar);
    document.body.classList.add("menu-open");
    return () => {
      document.removeEventListener("keydown", aoTeclar);
      document.body.classList.remove("menu-open");
    };
  }, [pedido, responder]);

  const tom = TONS[pedido?.tom] || TONS.coin;

  const dialogo = pedido
    ? createPortal(
        <div
          className="fixed inset-0 z-[10050] flex items-center justify-center bg-black/70 px-4 backdrop-blur-sm"
          onClick={() => responder(false)}
        >
          <div
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="confirmar-gasto-titulo"
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-sm bloco p-5 sm:p-6 text-white shadow-2xl"
          >
            <div className="flex items-center gap-3">
              <span className={"inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border text-lg " + tom.selo}>
                <i className={"bi " + tom.icone} aria-hidden="true" />
              </span>
              <h2 id="confirmar-gasto-titulo" className="text-lg font-bold leading-snug">
                {pedido.titulo}
              </h2>
            </div>

            {pedido.linhas?.length > 0 && (
              <dl className="mt-4 divide-y divide-white/10 rounded-xl border border-line bg-insigne-navy-deep/60 px-4">
                {pedido.linhas.map((l, i) => (
                  <div key={i} className="flex items-center justify-between gap-4 py-2.5 text-sm">
                    <dt className="text-white/55">{l.rotulo}</dt>
                    <dd className="text-right text-white">{l.valor}</dd>
                  </div>
                ))}
              </dl>
            )}

            {pedido.nota && <p className="mt-3 text-xs leading-relaxed text-white/55">{pedido.nota}</p>}

            <div className="mt-5 flex flex-col-reverse gap-2 sm:flex-row">
              <button
                type="button"
                onClick={() => responder(false)}
                className="foco-laranja flex-1 rounded-xl border border-line bg-white/5 py-2.5 text-sm font-semibold
                           text-white/85 transition-colors hover:bg-white/10"
              >
                Cancelar
              </button>
              <button
                type="button"
                autoFocus
                onClick={() => responder(true)}
                className={"foco-laranja flex-1 rounded-xl py-2.5 text-sm font-bold transition-colors " + tom.botao}
              >
                {pedido.textoConfirmar || "Confirmar"}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )
    : null;

  return { confirmar, dialogo };
}
